import React, { useEffect, useMemo, useState } from "react";
import { inputClass } from "../../../constants/formStyles.ts";

type FlatOptions = { [key: string]: string };
type GroupedOptions = { [group: string]: { [key: string]: string } };

interface SelectGroupProps {
  id?: string;
  options: FlatOptions | GroupedOptions;
  value?: any;
  placeholder?: string;
  name?: string;
  multiple?: boolean;
  classNameAdditional?: string;
  onChange?: (selectedValue: any, name: string) => void;
  shouldSort?: boolean;
  disabled?: boolean;
}

const NEW_ENTRY_VALUE = "0";

const isGrouped = (
  options: FlatOptions | GroupedOptions
): options is GroupedOptions =>
  Object.values(options).some((v) => typeof v === "object" && v !== null);

const sortEntries = (entries: [string, string][]): [string, string][] =>
  entries.sort((a, b) => {
    if (a[0] === NEW_ENTRY_VALUE) return -1;
    if (b[0] === NEW_ENTRY_VALUE) return 1;
    return String(a[1]).localeCompare(String(b[1]), "bg");
  });

// A component boundary is important here, not just a cached array. Without it,
// React still reconciles every cached <option> whenever the surrounding form
// changes. Stable children let React skip the whole nomenclature subtree.
const MemoizedOptionTree = React.memo<{ children: React.ReactNode }>(
  ({ children }) => <>{children}</>
);

const SelectGroup: React.FC<SelectGroupProps> = ({
  id,
  options = {},
  value,
  placeholder = "Изберете опция",
  classNameAdditional = "",
  onChange,
  multiple = false,
  shouldSort = true,
  name = "",
  disabled = false,
}) => {
  const [selectedOption, setSelectedOption] = useState<string>(value ?? "");

  useEffect(() => {
    setSelectedOption(value ?? "");
  }, [value]);

  // Some callers render the complete EKATTE directory (5,367 settlements).
  // Person forms update their parent on every keystroke; rebuilding and sorting
  // that directory in every sibling select made ordinary text input visibly lag.
  // Options are immutable between nomenclature refreshes, so retain the React
  // subtree until the catalogue or sorting mode actually changes.
  const renderedOptions = useMemo(() => {
    if (isGrouped(options)) {
      return Object.entries(options).map(([group, groupOptions]) => {
        const entries = Object.entries(groupOptions as FlatOptions);
        const sorted = shouldSort ? sortEntries(entries) : entries;
        return (
          <optgroup key={group} label={group}>
            {sorted.map(([key, label]) => (
              <option
                key={key}
                value={key}
                className="text-body dark:text-bodydark"
              >
                {label}
              </option>
            ))}
          </optgroup>
        );
      });
    }

    const entries = Object.entries(options as FlatOptions);
    const sorted = shouldSort ? sortEntries(entries) : entries;
    return sorted.map(([key, label]) => (
      <option key={key} value={key} className="text-body dark:text-bodydark">
        {label}
      </option>
    ));
  }, [options, shouldSort]);

  return (
    <div className="relative z-20 w-full bg-transparent dark:bg-form-input">
      <select
        id={id}
        value={selectedOption}
        name={name}
        multiple={multiple}
        disabled={disabled}
        onChange={(e) => {
          const selectedValue = e.target.value;
          setSelectedOption(selectedValue);
          if (onChange) onChange(selectedValue, e.target.name);
        }}
        className={`${inputClass} appearance-none pr-7 ${
          selectedOption ? "" : "text-gray-500"
        } ${
          disabled
            ? "bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500 cursor-not-allowed"
            : ""
        } ${classNameAdditional}`}
      >
        {!selectedOption && (
          <option value="" disabled hidden>
            {placeholder}
          </option>
        )}
        <MemoizedOptionTree>{renderedOptions}</MemoizedOptionTree>
      </select>
      <span
        className={`absolute right-2.5 top-1/2 z-30 -translate-y-1/2 text-xs ${
          disabled ? "opacity-50" : ""
        }`}
      >
        <i className="fa-solid fa-chevron-down" />
      </span>
    </div>
  );
};

export default SelectGroup;
